import React, { forwardRef, useCallback, useEffect, type ReactNode } from 'react';
import ModalDialog, { ModalBody } from '@atlaskit/modal-dialog';
import { UserTraitsProvider } from '@atlassian/jira-business-common';
import { isJwmReduceNoiseOnSignupEnabled } from '@atlassian/jira-business-feature-flags';
import { ClipboardBoundary } from '@atlassian/jira-clipboard';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries';
import { ff } from '@atlassian/jira-feature-flagging';
import { useInlineFields } from '@atlassian/jira-forge-controllers-inline-fields-state';
import { getErrorType } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-error-type';
import { ISSUE_CREATE_MODAL_BODY_SELECTOR } from '@atlassian/jira-issue-create-commons';
import { useFormContext } from '@atlassian/jira-issue-create-form-context';
import {
	FullscreenTransition,
	MiniTransition,
	MinimizableModal,
	MinimizedTransition,
	ModalTransition,
	useMinimizableModal,
	type MinimizableModalView,
} from '@atlassian/jira-minimizable-modal';
import {
	AiEnabledMiniTransition,
	AiEnabledMinimizedTransition,
} from '@atlassian/jira-minimizable-modal/src/ui/modal-container';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal';
import UFOSegment from '@atlassian/jira-ufo-segment';
import Footer from '../../../common/ui/footer';
import {
	useGICPositioningEnabledController,
	useIsGICPositioningEnabled,
} from '../../../controllers/gic-positioning-enabled';
import {
	MinimizableModalWrapperPropsProvider,
	useMinimizableModalWrapperProps,
	type MinimizableModalWrapperProps,
} from '../../../controllers/minimizable-modal-wrapper-props';
import { MinimizableAiEnabled } from './minimizable-ai-enabled';
import { MinimizableAiEnabledMinimized } from './minimizable-ai-enabled-minimized';
import { MinimizableFullscreen } from './minimizable-fullscreen';
import { MinimizableModalMini } from './minimizable-modal-mini';
import { MinimizableModalMinimized } from './minimizable-modal-minimized';
import { Header, MinimizableModalModal } from './minimizable-modal-modal';
import { useSetSummary } from './use-set-summary';
import { useViewChangerDisabler } from './use-view-changer-disabler';

const ModalBodyNext = forwardRef((props, ref) => (
	<ModalBody>
		{/* @ts-expect-error - TS2322 - Type '((instance: unknown) => void) | MutableRefObject<unknown> | null' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'. */}
		<div id={ISSUE_CREATE_MODAL_BODY_SELECTOR} ref={ref} {...props} />
	</ModalBody>
));

const OldModal = ({
	children,
	onModalCloseWithConfirm,
	shouldDisableActions,
	onSubmit,
	isSubmitting,
	isOperationInProgress,
	canCreateAnotherIssue,
	onCreateAnotherChange,
	getShouldDisableSubmit,
	createdIssues,
	displayCreateAnotherIssueToggle,
	showIssuesCreated,
	watchersConfigurations,
}: MinimizableModalWrapperProps) => (
	<ShortcutScope>
		<ModalDialog
			width="large"
			height="100vh"
			autoFocus={false}
			// @ts-expect-error - TS2322 - Type '(_: SyntheticEvent<Element, Event>, analyticsEvent: UIAnalyticsEventInterface) => void' is not assignable to type 'OnCloseHandler'.
			onClose={onModalCloseWithConfirm('issueCreateModalEscapeClick')}
			shouldCloseOnEscapePress
			shouldCloseOnOverlayClick={false}
			testId="issue-create.ui.modal.modal-wrapper.modal"
		>
			<Header
				shouldDisableActions={shouldDisableActions}
				watchersConfigurations={watchersConfigurations}
			/>
			{/* @ts-expect-error - TS2559 - Type '{ children: ReactNode; }' has no properties in common with type 'IntrinsicAttributes & RefAttributes<unknown>'. */}
			<ModalBodyNext>{children}</ModalBodyNext>
			<Footer
				onClose={onModalCloseWithConfirm('cancel')}
				onSubmit={onSubmit}
				isSubmitting={isSubmitting}
				isOperationInProgress={isOperationInProgress}
				canCreateAnotherIssue={canCreateAnotherIssue}
				onCreateAnotherChange={onCreateAnotherChange}
				shouldDisableSubmit={getShouldDisableSubmit()}
				createdIssues={createdIssues}
				displayCreateAnotherIssueToggle={displayCreateAnotherIssueToggle}
				showIssuesCreated={showIssuesCreated}
			/>
		</ModalDialog>
	</ShortcutScope>
);

const WrapWithUserTraitsProvider = ({ children }: { children: React.ReactNode }) =>
	isJwmReduceNoiseOnSignupEnabled() ? (
		<UserTraitsProvider>{children}</UserTraitsProvider>
	) : (
		children
	);

const MinimizableModalContainer = ({ children }: { children?: ReactNode }) => {
	const { initialSummaryValue } = useMinimizableModalWrapperProps();

	const { view } = useMinimizableModal();

	const summaryValue = useSetSummary(initialSummaryValue);

	useViewChangerDisabler();

	return (
		<>
			<ModalTransition renderCondition={view === 'modal'}>
				<MinimizableModalModal data-testid="issue-create.ui.modal.modal-wrapper.modal">
					{children}
				</MinimizableModalModal>
			</ModalTransition>
			<MiniTransition renderCondition={view === 'mini'}>
				{({ transitionProps }) => (
					<WrapWithUserTraitsProvider>
						<MinimizableModalMini {...transitionProps} summaryValue={summaryValue}>
							{children}
						</MinimizableModalMini>
					</WrapWithUserTraitsProvider>
				)}
			</MiniTransition>
			{ff('jira-intelligent-issue-breakdown_18syl') && (
				<AiEnabledMiniTransition renderCondition={view === 'ai-enabled'}>
					{({ transitionProps }) => (
						<MinimizableAiEnabled {...transitionProps}>{children}</MinimizableAiEnabled>
					)}
				</AiEnabledMiniTransition>
			)}
			<FullscreenTransition renderCondition={view === 'fullscreen'}>
				{({ transitionProps }) => (
					<MinimizableFullscreen {...transitionProps}>{children}</MinimizableFullscreen>
				)}
			</FullscreenTransition>
			<MinimizedTransition renderCondition={view === 'minimized'}>
				{({ transitionProps }) => (
					<MinimizableModalMinimized {...transitionProps} summaryValue={summaryValue} />
				)}
			</MinimizedTransition>
			{ff('jira-intelligent-issue-breakdown_18syl') && (
				<AiEnabledMinimizedTransition renderCondition={view === 'ai-enabled-minimized'}>
					{({ transitionProps }) => <MinimizableAiEnabledMinimized {...transitionProps} />}
				</AiEnabledMinimizedTransition>
			)}
		</>
	);
};

const OldModalWrapper = (props: MinimizableModalWrapperProps) => {
	const [{ disableGICPositioning }, { setDisableGICPositioning }] =
		useGICPositioningEnabledController();
	useEffect(() => {
		if (!disableGICPositioning) {
			setDisableGICPositioning(true);
		}
	}, [disableGICPositioning, setDisableGICPositioning]);
	return <OldModal {...props} />;
};

export const ModalWrapper = ({ children, ...rest }: MinimizableModalWrapperProps) => {
	const [{ minimizableModalView, displayMinimizableButtons }, { setMinimizableModalView }] =
		useTriggerIssueCreateModal();
	const isGICPositioningEnabled = useIsGICPositioningEnabled();
	const [, { dispatchFormSubmits: dispatchForgeInlineFormSubmits }] = useInlineFields();
	const [{ isFormLoaded }] = useFormContext();

	const renderOldModal = () => <OldModalWrapper {...rest}>{children}</OldModalWrapper>;

	const onViewChange = useCallback(
		(newView: MinimizableModalView) => {
			setMinimizableModalView(newView);
			dispatchForgeInlineFormSubmits();
		},
		[setMinimizableModalView, dispatchForgeInlineFormSubmits],
	);

	if (isGICPositioningEnabled()) {
		return (
			<UFOSegment name="issue-create-modal">
				<JSErrorBoundary
					id={
						ff('gic-modal-js-error-boundary-post-load-errors_qh9ev') && isFormLoaded
							? 'issue-create-app-loaded'
							: 'issue-create-app'
					}
					/* eslint-disable-next-line jira/js/package-name-enforcer-for-error-boundary */
					packageName="jiraIssueCreateModal"
					fallback={renderOldModal}
					sendToPrivacyUnsafeSplunk
					attributes={(error: Error) => ({
						errorType: getErrorType(error),
					})}
				>
					<MinimizableModalWrapperPropsProvider {...rest}>
						<MinimizableModal
							view={minimizableModalView}
							onViewChange={onViewChange}
							Container={MinimizableModalContainer}
							// @ts-expect-error - TS2322 - Type '(_: SyntheticEvent<Element, Event>, analyticsEvent: UIAnalyticsEventInterface) => void' is not assignable to type 'OnCloseHandler'.
							onClose={rest.onModalCloseWithConfirm('cancel')}
							displayMinimizableButtons={displayMinimizableButtons}
						>
							<ClipboardBoundary>{children}</ClipboardBoundary>
						</MinimizableModal>
					</MinimizableModalWrapperPropsProvider>
				</JSErrorBoundary>
			</UFOSegment>
		);
	}
	return renderOldModal();
};
